// 部門編輯 modal（config-department「新增一級部門／新增子部門／修改一級部門／修改子部門」
// ＝legacy `#dialogModal > #itemForm`）。
//
// legacy 流程（行號逐檔 grep -n 親驗，2026-09-23；詳 docs/plans/2026-09-22-batch12-config-modal-progress.md §1.1）：
//   - showModal（department.jsp:41-136）：四態層級擇一——
//       新增一級部門（:56）：parentId=0（:55）、醫院顯示可選（val '' → 呈現選項首位的 請選擇-1，:57）、
//         父部門藏（:59-60）。
//       新增子部門（:66）：parentId=node.id（:63）、醫院藏（:68）、父部門顯示且 disabled（:64）、
//         選項=getSameLevelDepartmentListByParentId(parentId=node.id)（:70）、selected=node.id（:76）。
//       修改一級部門（:93）：organizationId 回填＋disabled（:89-90）、醫院顯示、父部門藏（:95-96）。
//       修改子部門（:98）：parentId=node.data.parentId（:99）、父部門顯示可選（:100）、
//         選項=…(parentId=node.data.parentId)（:109）、selected=node.data.parentId（:115）。
//     updateUserName/updateDate :124-125、updateInfoDiv show :126（新增模式 hide :86）。
//   - showTableModal（列表修改，:131-190）：停用列（isEnable==0）→ savebtn hide＋
//     name/shortName/sort/code disabled（:135-139）；啟用列恢復＋savebtn show（:141-145）；
//     組織/父部門擇一同 showModal（:148-161）；updateUserName/updateDateTime :183-184、show :185。
//   - save（:326-357）：required 屬性（醫院/部門代碼/部門名稱）→ jquery.validate inline
//     `必填欄位`（decorator.jsp:262）；organizationId==-1 → showAlertDialog('請選擇醫院')（:336-338）；
//     修改子部門時 parentId=parentIdList.val()（:341-342）；sort 空→'0'（:344）；
//     POST config/saveDepartmentListAjax（:346）；成功 → doQuery＋showInfoDialog(desc)＋modal hide
//     （:349-352）；失敗 → doQuery＋showAlertDialog(desc)（:355-356）。
//
// 雛形資料策略：
//   - 存檔走既有 `dataSource.create/update('config-department',…)`（D-1 五個 op 之內）；
//     真呼叫 DataSource 的存檔鈕才帶 data-effect（C5-j）。
//   - 部門代碼欄＝mock 列鍵 id：輸入框照 legacy 可輸入（required 45），但 `MockDataSource.update`
//     不覆寫 id／create 的 id 由 buildId 產生——既有列改碼無效、新列代碼為產生值
//     （mock 語意差異，進度檔 §6.1）。
//   - 父部門選項＝同層清單鏡像：getSameLevelDepartmentListByParentId 的 service 不在 repo
//     （DepartmentController.java:94），語意由兩個呼叫端反推＝「該 id 與其兄弟」
//     （新增子部門 selected=node.id :76、修改子部門 selected=node.data.parentId :115 都必須在
//     清單內）→ 選項＝與參考節點同 parentId 的列（含參考節點）【雛形值待實機校準】。
//   - 醫院選項＝`optionsFor(pageId, organizationId 欄)`（與查詢表單/樹根同源）＋最前面
//     `請選擇`（value=-1，setOrganizationElement isAddFindAll=false，ePortfolio.js:1229）。
//   - 異動人員/日期：修改模式顯示（:124-125/:183-184），值為種子【雛形值待實機校準】。
//
// 【證據等級】§7 第 3 級（原始碼）。

import { useEffect, useState, type ReactElement } from 'react';
import { Button } from '@/components/base/Button';
import { Select } from '@/components/base/Select';
import { TextInput } from '@/components/base/TextInput';
import { ModalFrame, useDialogs } from '@/components/layouts';
import { t } from '@/messages/zh-TW';
import {
  MockDataError,
  legacyContractFieldsFor,
  optionsFor,
  resolveLegacyMessage,
  useDataSource,
} from '@/mocks';
import type { Row } from '@/mocks/types';
import { toUiKey } from '@/pages/templates/shared';

export interface DepartmentEditModalProps {
  readonly pageId: string;
  /** 'create'＝新增（showModal(0,…)）；'edit'＝修改（showModal(id,node)/showTableModal）。 */
  readonly mode: 'create' | 'edit';
  /** 修改時的列（樹節點／列表列；新增時 null）。 */
  readonly row: Row | null;
  /** editing.values（批次 12 editModal hook 補傳）：樹「新增子部門」的 parentId preset（CrudListPage openCreate）。 */
  readonly values: Record<string, string>;
  readonly onClose: () => void;
  readonly onClosed: () => void;
}

/** jquery.validate 全域 required 訊息（decorator.jsp:262）。 */
const REQUIRED_TEXT = '必填欄位';

/** 醫院下拉的「請選擇」哨兵（setOrganizationElement isAddFindAll=false，ePortfolio.js:1229；value=-1）。 */
const ORGANIZATION_PLACEHOLDER = '請選擇';
const ORGANIZATION_SENTINEL = '-1';

/**
 * 同層清單（getSameLevelDepartmentListByParentId 鏡像，D2）：與 refId 列同 parentId 的所有列
 * （含 refId 本身）；R2-F3：legacy AJAX 同時帶 organizationId 醫院過濾（department.jsp:70/:109/:168），
 * 醫院值已知時一併過濾（種子單一醫院時無差異）。label＝部門名稱（department.jsp :76/:115 的 d.name）。
 */
function sameLevelOptions(
  rows: readonly Row[],
  refId: string,
  organizationId: string,
): readonly { value: string; label: string }[] {
  const ref = rows.find((row) => String(row.id ?? '') === refId);
  const parentOfRef = String(ref?.['parentId'] ?? '0');
  return rows
    .filter(
      (row) =>
        String(row['parentId'] ?? '0') === parentOfRef &&
        (organizationId === '' || String(row['organizationId'] ?? '') === organizationId),
    )
    .map((row) => ({ value: String(row.id ?? ''), label: String(row['unit'] ?? '') }));
}

export function DepartmentEditModal({
  pageId,
  mode,
  row,
  values,
  onClose,
  onClosed,
}: DepartmentEditModalProps): ReactElement {
  const dataSource = useDataSource();
  const dialogs = useDialogs();

  /** 取消／關閉：只關 modal。legacy department.jsp/annual.jsp 沒有 hidden.bs.modal → doQuery
   * 的掛載（doQuery 只在 ready/save 後，R2-F5）；reload 由存檔流程自己帶。 */
  const closeMainModal = () => {
    onClose();
  };
  /** 存檔後的清單刷新（legacy save() 成功/失敗都先 doQuery，department.jsp:348/:356）。 */
  const reloadList = () => {
    onClosed();
  };

  // ------------------------------------------------------------------
  // 層級判定（department.jsp:41-136）。
  //   修改：row.parentId=='0' → 一級；新增：樹 preset values.parentId（≠'0'）→ 子部門，
  //   工具列「新增一級部門」無 preset → 一級。
  // ------------------------------------------------------------------
  const isEdit = mode === 'edit' && row !== null;
  const parentRef = isEdit
    ? String(row?.['parentId'] ?? '0')
    : String(values['parentId'] ?? '0');
  const isTopLevel = parentRef === '0';
  // 停用鎖（R2-F2）：**只有列表入口 showTableModal 有**（department.jsp:134-145 savebtn hide＋
  // name/shortName/sort/code disabled）；樹入口 showModal（:41-136）無 isEnable 分支，停用列
  // 照樣可編輯可存檔。入口由 CrudListPage openEdit preset 帶 editEntry='tree' 區分。
  const locked =
    isEdit &&
    String(row?.['status'] ?? '') === '停用' &&
    String(values['editEntry'] ?? '') !== 'tree';

  // ------------------------------------------------------------------
  // 表單值（showModal :48-57 新增／:88-106 修改；showTableModal :147-165）。
  // ------------------------------------------------------------------
  const [code, setCode] = useState(isEdit ? String(row?.id ?? '') : ''); // 部門代碼（:52/:91）
  const [name, setName] = useState(isEdit ? String(row?.['unit'] ?? '') : ''); // 部門名稱（:49/:104）
  const [shortName, setShortName] = useState(isEdit ? String(row?.['shortName'] ?? '') : ''); // :50/:105
  const [sort, setSort] = useState(isEdit ? String(row?.['score'] ?? '') : '0'); // :51 sort val(0)／:106
  // 醫院（:89-90 回填＋disabled；新增一級 val('') → 呈現 請選擇-1）。
  const [organizationId, setOrganizationId] = useState(
    isEdit ? String(row?.['organizationId'] ?? '') : ORGANIZATION_SENTINEL,
  );
  // 父部門（parentIdList）：新增子部門＝preset（selected=node.id :76）；修改子部門＝row.parentId
  // （selected=node.data.parentId :115，可改選同層）。
  const [parentIdList, setParentIdList] = useState(parentRef);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  // 全部列（同層選項＋新增子部門繼承父部門的醫院 :62 用）；列表本身已在背景載過一次，
  // 這裡的 list 是讀 op、沒有任何按鈕宣稱它（C5-j 不需 data-effect）。
  const [rows, setRows] = useState<readonly Row[]>([]);
  useEffect(() => {
    let alive = true;
    void dataSource
      .list(pageId)
      .then((result) => {
        if (alive) {
          setRows(result.rows);
        }
      })
      .catch(() => {
        // 選項來源失敗就留空（legacy 這支 AJAX 失敗也只是 console.log，:78-81）。
      });
    return () => {
      alive = false;
    };
  }, [dataSource, pageId]);

  // 醫院選項（與查詢表單同一份 contract 欄位；樹根標籤同源）。
  const organizationField = legacyContractFieldsFor(pageId)?.find(
    (field) => field.name === 'organizationId',
  );
  const organizationOptions = [
    { value: ORGANIZATION_SENTINEL, label: ORGANIZATION_PLACEHOLDER },
    ...(organizationField === undefined
      ? []
      : optionsFor(pageId, organizationField).map((option) => ({
          value: option.value,
          label: option.text,
        }))),
  ];
  // 新增子部門：醫院繼承父部門（legacy setOrganizationElement(theForm…, node.data.organizationId)，
  // :62）；修改：自身列的醫院（:89）。同步取值（樹 preset 已帶 organizationId；R2-F4），
  // 父列查找僅作為舊資料（無 preset）的後備。
  const parentRow = rows.find((row_) => String(row_?.id ?? '') === parentRef);
  const inheritedOrganizationId = isEdit
    ? String(row?.['organizationId'] ?? '')
    : String(
        values['organizationId'] !== undefined && values['organizationId'] !== ''
          ? values['organizationId']
          : (parentRow?.['organizationId'] ?? ''),
      );

  const title = isEdit
    ? isTopLevel
      ? '修改一級部門' // department.jsp:93（樹）/:152（列表）
      : '修改子部門' // :98/:157
    : isTopLevel
      ? '新增一級部門' // :56
      : '新增子部門'; // :66

  const alertOrGeneric = async (cause: unknown) => {
    await dialogs.alert(cause instanceof MockDataError ? cause.message : t('error.generic'));
  };

  const saveSuccessText = () =>
    resolveLegacyMessage(pageId, 'success')?.text ?? t('state.saveSuccess');

  // ------------------------------------------------------------------
  // 存檔（legacy save，department.jsp:326-357）
  // ------------------------------------------------------------------
  const save = () => {
    void (async () => {
      // required 屬性（醫院/部門代碼/部門名稱，:688/:704/:712）→ jquery.validate inline
      // `必填欄位`（decorator.jsp:262）。醫院的 請選擇-1 sentinel 非空值，required 不擋（legacy 同）——
      // 未選醫院的攔截在下方 alert（:336-338）；code/name 在這裡 inline 擋。
      const errors: Record<string, string> = {};
      if (code.trim() === '') {
        errors.code = REQUIRED_TEXT;
      }
      if (name.trim() === '') {
        errors.name = REQUIRED_TEXT;
      }
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
      setFieldErrors({});
      // :336-338（value==-1 → showAlertDialog('請選擇醫院')）。
      if (isTopLevel && (organizationId === ORGANIZATION_SENTINEL || organizationId === '')) {
        await dialogs.alert('請選擇醫院');
        return;
      }
      // :341-342：修改子部門時 parentId ← parentIdList.val()；新增子部門＝preset（:63）；
      // 一級恆 '0'（:55/:94）。:344：sort 空 → '0'。
      const nextParentId = isEdit && !isTopLevel ? parentIdList : parentRef;
      const payload: Row = {
        // 部門代碼欄（mock：update 不覆寫 id／create 由 buildId 產生，進度檔 §6.1）。
        id: code,
        parentId: nextParentId,
        unit: name,
        shortName,
        score: sort === '' ? '0' : sort,
      };
      if (isTopLevel) {
        payload.organizationId = organizationId;
      } else if (inheritedOrganizationId !== '') {
        // 新增/修改子部門：legacy 表單 organizationId 由 setOrganizationElement 帶父部門的醫院
        // （:62），hidden 欄照序列化；雛形以父列的 organizationId 鏡像。
        payload.organizationId = inheritedOrganizationId;
      }
      setBusy(true);
      try {
        if (isEdit) {
          await dialogs.processing.wrap(
            dataSource.update(pageId, String(row?.id ?? ''), payload),
          );
        } else {
          await dialogs.processing.wrap(dataSource.create(pageId, payload));
        }
        // 成功：doQuery＋showInfoDialog(desc)＋modal hide（:349-352）→ 關 modal＋刷新＋訊息。
        closeMainModal();
        reloadList();
        await dialogs.info(saveSuccessText());
      } catch (cause: unknown) {
        // 失敗：doQuery＋showAlertDialog(desc)（:355-356）→ 刷新但 modal 不關。
        reloadList();
        const fields = cause instanceof MockDataError ? { ...cause.fields } : {};
        if (Object.keys(fields).length > 0) {
          setFieldErrors(fields);
          return;
        }
        await alertOrGeneric(cause);
      } finally {
        setBusy(false);
      }
    })();
  };

  // 欄位共用樣式（legacy label col-sm-3 + control col-sm-4 的橫向表單）。
  // 批次 50（工作項 4）：key（payload key 語意）入 uiId/htmlFor 的字尾一律 toUiKey kebab 化
  // （§10.3 規則 1；key 參數本身不動＝fieldErrors[key] 域零影響）。
  const fieldLabel = (key: string, text: string, required: boolean) => (
    <label className="shrink-0 text-sm" htmlFor={`${pageId}-form-dialog-input-${toUiKey(key)}`}>
      {required ? <span className="text-danger">*</span> : null}
      {text}
    </label>
  );
  const fieldError = (key: string) => {
    const message = fieldErrors[key];
    return message === undefined ? null : (
      <div data-ui-id={`${pageId}-form-dialog-error-${toUiKey(key)}`} className="text-xs text-danger">
        {message}
      </div>
    );
  };

  return (
    <ModalFrame
      uiId={`${pageId}-form-dialog`}
      open
      title={title}
      size="lg"
      onClose={closeMainModal}
      footer={
        <>
          <Button
            uiId={`${pageId}-form-dialog-cancel`}
            variant="default"
            size="sm"
            type="button"
            onClick={closeMainModal}
          >
            {/* department.jsp:741（btn-close data-dismiss） */}
            關閉
          </Button>
          {/* :743（btn-add id=savebtn onclick=save()）；停用列 savebtn.hide（:138）。 */}
          {locked ? null : (
            <Button
              uiId={`${pageId}-form-dialog-save`}
              variant="primary"
              size="sm"
              type="button"
              disabled={busy}
              // C5-j：存檔真的呼叫 dataSource.create/update。
              dataEffect={`${isEdit ? 'update' : 'create'}:${pageId}`}
              onClick={save}
            >
              存檔
            </Button>
          )}
        </>
      }
    >
      <div data-ui-id={`${pageId}-form-dialog-body`} className="space-y-3">
        {/* 醫院（:686-694）：一級顯示（新增可選／修改 disabled 回填）；子部門藏（:68/:101）。 */}
        {isTopLevel ? (
          <div className="flex items-start gap-2">
            {fieldLabel('organizationId', '醫院：', true)}
            <div>
              <Select
                uiId={`${pageId}-form-dialog-input-organization-id`}
                value={organizationId}
                onChange={setOrganizationId}
                options={organizationOptions}
                disabled={isEdit || busy}
              />
              {fieldError('organizationId')}
            </div>
          </div>
        ) : null}
        {/* 父部門（:696-701）：子部門顯示（新增 disabled selected=node.id :64/:76；修改可選 :100）。 */}
        {!isTopLevel ? (
          <div className="flex items-start gap-2">
            {fieldLabel('parentIdList', '父部門：', true)}
            <div>
              <Select
                uiId={`${pageId}-form-dialog-input-parent-id-list`}
                value={parentIdList}
                onChange={setParentIdList}
                options={sameLevelOptions(rows, parentRef, inheritedOrganizationId)}
                disabled={!isEdit || busy}
              />
              {/* 同層選項＝getSameLevelDepartmentListByParentId 鏡像【雛形值待實機校準】。 */}
              {fieldError('parentIdList')}
            </div>
          </div>
        ) : null}
        {/* 部門代碼（:702-708，required 45）；＝mock 列鍵 id（進度檔 §6.1）。 */}
        <div className="flex items-start gap-2">
          {fieldLabel('code', '部門代碼：', true)}
          <div>
            <TextInput
              uiId={`${pageId}-form-dialog-input-code`}
              value={code}
              onChange={setCode}
              maxLength={45}
              disabled={locked || busy}
              invalid={fieldErrors.code !== undefined}
            />
            {fieldError('code')}
          </div>
        </div>
        {/* 部門名稱（:709-716，required 40）。 */}
        <div className="flex items-start gap-2">
          {fieldLabel('name', '部門名稱：', true)}
          <div>
            <TextInput
              uiId={`${pageId}-form-dialog-input-name`}
              value={name}
              onChange={setName}
              maxLength={40}
              disabled={locked || busy}
              invalid={fieldErrors.name !== undefined}
            />
            {fieldError('name')}
          </div>
        </div>
        {/* 部門簡稱（:717-723，15）。 */}
        <div className="flex items-start gap-2">
          {fieldLabel('shortName', '部門簡稱：', false)}
          <TextInput
            uiId={`${pageId}-form-dialog-input-short-name`}
            value={shortName}
            onChange={setShortName}
            maxLength={15}
            disabled={locked || busy}
          />
        </div>
        {/* 排序序號（:724-731，number 0-99999；雛形以文字輸入呈現，範圍由存檔語意鏡像 :344）。 */}
        <div className="flex items-start gap-2">
          {fieldLabel('sort', '排序序號：', false)}
          <TextInput
            uiId={`${pageId}-form-dialog-input-sort`}
            value={sort}
            onChange={setSort}
            disabled={locked || busy}
          />
        </div>
        {/* 異動人員/日期（updateInfoDiv :732-741）：修改模式顯示（:124-125/:183-184），新增隱藏（:86）。 */}
        {isEdit ? (
          <div data-ui-id={`${pageId}-form-dialog-update-info`} className="space-y-2 pt-2">
            <div className="flex items-center gap-2">
              <label className="shrink-0 text-sm">異動人員：</label>
              <span data-ui-id={`${pageId}-form-dialog-input-update-user-name`} className="text-sm">
                {String(row?.['updateUserName'] ?? '')}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <label className="shrink-0 text-sm">異動日期：</label>
              <span data-ui-id={`${pageId}-form-dialog-input-update-date`} className="text-sm">
                {String(row?.['updateDate'] ?? row?.['updateDateTime'] ?? '')}
              </span>
            </div>
          </div>
        ) : null}
      </div>
    </ModalFrame>
  );
}
